import React from 'react';
import { MdChevronRight } from 'react-icons/md';
import { FOCUS_RING, cx } from './toneStyles';

/**
 * ListRow — one row of a list surface (histories, settings, More page).
 *
 * Props
 *   leading      node (icon disc / Avatar / emoji content) on the left
 *   title        primary text (string or node)
 *   subtitle     secondary text
 *   trailing     node on the right (time, Pill, IconButton, ToggleSwitch)
 *   onPress      makes the row a 44px+ button; shows a chevron when there is no trailing
 *                or actions
 *   actions      node rendered BESIDE a pressable row's button, outside it (e.g. the delete
 *                IconButton on a tap-to-edit history row)
 *   destructive  red title (delete / sign out rows)
 *   divider      adds a bottom border (ListGroup already divides its children)
 *   as           outer element override (e.g. 'li'); with onPress the inner button stays
 *
 * Interactive controls (IconButton, ToggleSwitch) go in `trailing` only on a non-pressable
 * row; on a pressable row they go in `actions`, or they'd nest inside the row button.
 */
export default function ListRow({
  leading,
  title,
  subtitle,
  trailing,
  onPress,
  actions,
  destructive = false,
  divider = false,
  as,
  className = '',
  ...props
}) {
  const content = (
    <>
      {leading && <div className="flex-shrink-0 flex items-center">{leading}</div>}
      <div className="flex-1 min-w-0">
        <div
          className={cx(
            'text-sm font-medium truncate',
            destructive ? 'text-danger-600 dark:text-danger-400' : 'text-ink dark:text-night-text'
          )}
        >
          {title}
        </div>
        {subtitle && <div className="text-xs text-ink-subtle dark:text-night-muted truncate mt-0.5">{subtitle}</div>}
      </div>
      {trailing && <div className="flex-shrink-0 flex items-center gap-1 text-sm text-ink-subtle dark:text-night-muted">{trailing}</div>}
      {onPress && !trailing && !actions && (
        <MdChevronRight className="w-5 h-5 flex-shrink-0 text-ink-faint dark:text-night-muted" aria-hidden="true" />
      )}
    </>
  );

  const dividerCls = divider && 'border-b border-surface-200 dark:border-night-border last:border-b-0';
  const rowCls = cx('flex items-center gap-3 w-full min-h-[52px] px-3 py-3 text-left', dividerCls, className);

  if (onPress) {
    const pressCls = cx(
      'hover:bg-surface-50 active:bg-surface-100 dark:hover:bg-night-card dark:active:bg-night-border',
      'active:scale-[0.98] transition-[background-color,transform] duration-fast ease-out',
      FOCUS_RING,
      'focus-visible:ring-inset focus-visible:ring-offset-0'
    );

    if (actions) {
      const Outer = as || 'div';
      return (
        <Outer className={cx('flex items-center list-none', dividerCls, className)}>
          <button
            type="button"
            onClick={onPress}
            className={cx('flex items-center gap-3 flex-1 min-w-0 min-h-[52px] pl-3 pr-1 py-3 text-left', pressCls)}
            {...props}
          >
            {content}
          </button>
          <div className="flex-shrink-0 flex items-center gap-1 pr-1">{actions}</div>
        </Outer>
      );
    }

    const button = (
      <button type="button" onClick={onPress} className={cx(rowCls, pressCls)} {...props}>
        {content}
      </button>
    );
    if (as && as !== 'button') {
      const Outer = as;
      return <Outer className="list-none">{button}</Outer>;
    }
    return button;
  }

  const Tag = as || 'div';
  return (
    <Tag className={rowCls} {...props}>
      {content}
    </Tag>
  );
}
